import { useEffect, useState } from "react";
import { PaymentStatus } from "@repo/database";
import { differenceInSeconds } from "date-fns";

import type { Order } from "~/providers/order-provider";
import type { Section } from "~/providers/section-provider";
import { useConfiguration } from "~/hooks/use-configuration";
import { useDragScroll } from "~/hooks/use-drag-scroll";
import { useOrder } from "~/hooks/use-order";
import { useSection } from "~/hooks/use-section";
import { useTable } from "~/hooks/use-table";
import { cn } from "~/lib/utils";
import { useGeneralStore } from "~/stores/use-general-store";
import { useRegisterStore } from "~/stores/use-register-store";
import { RegisterButton } from "../ui/register-button";

const SectionButton = ({ section }: { section: Section }) => {
  const { selectedSection, setSelectedSection } = useRegisterStore();

  const setFloorPlanPosition = useGeneralStore(
    (state) => state.setFloorPlanPosition,
  );

  const [isLate, setIsLate] = useState(false);

  const [isAttention, setIsAttention] = useState(false);

  const { orders } = useOrder();

  const { cashRegisterConfiguration } = useConfiguration();

  const { tables } = useTable();

  const tablesInSection = tables.filter(
    (table) => table.locationId === section.id,
  );

  const [oldestOrderInSection, setOldestOrderInSection] = useState<
    Order | undefined
  >();

  const handleClick = (section: Section) => {
    setFloorPlanPosition({ x: 0, y: 0 });
    setSelectedSection(section);
  };

  useEffect(() => {
    if (!oldestOrderInSection) return;

    const updateSecondsSinceLastInteraction = () => {
      const diff = differenceInSeconds(
        Date.now(),
        oldestOrderInSection.updatedAt,
      );

      if (
        Math.abs(diff) > cashRegisterConfiguration.orderAttentionLateThreshold
      ) {
        setIsLate(true);
      } else if (
        Math.abs(diff) > cashRegisterConfiguration.orderAttentionCheckThreshold
      ) {
        setIsAttention(true);
      }
    };

    updateSecondsSinceLastInteraction();

    const interval = setInterval(() => {
      updateSecondsSinceLastInteraction();
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [
    cashRegisterConfiguration.orderAttentionCheckThreshold,
    cashRegisterConfiguration.orderAttentionLateThreshold,
    oldestOrderInSection,
  ]);

  useEffect(() => {
    setOldestOrderInSection(
      orders
        .filter(
          (order) =>
            tablesInSection.some((table) => table.id === order.tableId) &&
            order.paymentStatus === PaymentStatus.pending,
        )
        .sort((a, b) => a.updatedAt.getTime() - b.updatedAt.getTime())[0],
    );
  }, [orders, tablesInSection]);

  return (
    <RegisterButton
      borderClassName={cn(
        "border-2",
        oldestOrderInSection && !isLate && !isAttention && "border-green-400",
        oldestOrderInSection && isAttention && "border-yellow-400",
        oldestOrderInSection && isLate && "border-red-400",
      )}
      variant={selectedSection?.id === section.id ? "primary" : "default"}
      aria-current={selectedSection?.id === section.id ? "true" : "false"}
      onClick={() => {
        handleClick(section);
      }}
      size="dashboard"
    >
      {section.name}
    </RegisterButton>
  );
};

export function SectionButtons() {
  const { sections } = useSection();
  const [ref] = useDragScroll();

  return (
    <div
      ref={ref}
      className="hide-scrollbar p-1 h-16 flex flex-row items-center gap-1 overflow-auto"
    >
      {sections
        .sort((a, b) => a.position - b.position)
        .map((section) => (
          <SectionButton key={section.id} section={section} />
        ))}
    </div>
  );
}
